#!/usr/bin/env node
// answer-input.mjs — canonicalise and RISK-GATE a candidate's answer before it reaches the grader.
// Channel-agnostic by design: the same layer serves speech and typing (founder, 2026-08-01: "stt can
// also be enhanced as a typed interface"). Speech and a hurried thumb-typed answer fail in the same
// places — abbreviations, numbers, and dropped negations.
//
// The engine layer already exists and is not rebuilt here: ankr-sahmati/src/recognizer.ts abstracts
// Android SpeechRecognizer / iOS SFSpeechRecognizer / bundled whisper.cpp or Vosk behind one interface
// returning { text, confidence, alternatives[] } plus an onDevice flag. This module consumes that.
//
// WHY A GATE AND NOT JUST A CLEANER. The output feeds socratic-select, whose contradiction detector
// flags a candidate for asserting what a clause forbids. On the loll entry the corpus forbids "high
// side" and prescribes "low side" — one mis-recognised word inverts a CORRECT answer into a flagged
// error, and the candidate is told they are dangerously wrong when they were right. Several maritime
// pairs invert meaning like this, and ASR drops short function words like "not" routinely.
//
// So the rule is: where a flagged term could invert the meaning, DO NOT GRADE — ask one confirming
// question. Abstention is cheap; a false "you are wrong" on a life-safety trap is not.
//
//   node answer-input.mjs check "<transcript>" [confidence]
/* No `node:fs` import here. It was present and NEVER USED — a dead import, and in a browser bundle
   a dead import of a node builtin still fails to resolve. Removed rather than stubbed. */

// pairs where mishearing/mistyping INVERTS the meaning rather than degrading it
export const INVERTING_PAIRS = [
  ['low side', 'high side'], ['port', 'starboard'], ['hogging', 'sagging'],
  ['list', 'loll'], ['above', 'below'], ['increase', 'decrease'], ['increases', 'decreases'],
  ['windward', 'leeward'], ['more than', 'less than'], ['greater', 'smaller'],
  ['ahead', 'astern'], ['ebb', 'flood'], ['rise', 'fall'], ['raises', 'lowers'],
  ['higher', 'lower'], ['forward', 'aft'], ['add', 'remove'], ['loaded', 'discharged'],
  ['positive', 'negative'], ['stiff', 'tender'], ['salt water', 'fresh water'],
];
const NEGATIONS = /\b(not|never|no|cannot|don't|doesn't|shouldn't|must not|shall not)\b/i;

// spoken / loose forms -> the corpus's canonical token. Mirrors voice.mjs speakable() in reverse.
const CANON = [
  [/\b(um+|uh+|er+|you know|i think|i mean|like,)\b/gi, ''],
  [/\bg\.?\s?m\.?\b/gi, 'GM'], [/\bgee\s?em\b/gi, 'GM'],
  [/\bk\.?\s?g\.?\b/gi, 'KG'], [/\bk\.?\s?m\.?\b/gi, 'KM'], [/\bk\.?\s?b\.?\b/gi, 'KB'],
  [/\bg\.?\s?z\.?\b/gi, 'GZ'], [/\bgee\s?(zed|zee)\b/gi, 'GZ'],
  [/\bt\.?\s?p\.?\s?c\.?\b/gi, 'TPC'], [/\bm\.?\s?c\.?\s?t\.?\s?c\.?\b/gi, 'MCTC'],
  [/\bf\.?\s?s\.?\s?e\.?\b/gi, 'FSE'], [/\bu\.?\s?k\.?\s?c\.?\b/gi, 'UKC'],
  [/\bs\.?\s?w\.?\s?l\.?\b/gi, 'SWL'], [/\bv\.?\s?g\.?\s?m\.?\b/gi, 'VGM'],
  [/\bt\.?\s?m\.?\s?l\.?\b/gi, 'TML'],
  [/\bso(l|w)las\b/gi, 'SOLAS'], [/\bcol\s?regs?\b/gi, 'COLREG'], [/\bmar\s?pol\b/gi, 'MARPOL'],
  [/\bim\s?d\s?g\b/gi, 'IMDG'], [/\bim\s?s\s?b\s?c\b/gi, 'IMSBC'],
  [/\bs\.?\s?t\.?\s?c\.?\s?w\.?\b/gi, 'STCW'],
  [/\bchapter\s+two\s+dash\s+one\b/gi, 'Ch. II-1'], [/\bchapter\s+five\b/gi, 'Ch. V'],
  [/\bso\s?las\b/gi, 'SOLAS'], [/\bsow\s?las\b/gi, 'SOLAS'],
  [/\bmetre\s?radians?\b/gi, 'm·rad'], [/\bnewtons per square millimetre\b/gi, 'N/mm2'],
  [/\bdegrees? celsius\b/gi, '°C'],
];
const NUMWORD = { nought:0, oh:0, o:0, zero:0, one:1, two:2, three:3, four:4, five:5, six:6, seven:7, eight:8, nine:9 };
const DIGITS = '(?:nought|oh|zero|one|two|three|four|five|six|seven|eight|nine|\\d)';

/** Canonicalise a candidate answer from any channel. Lookup and rewrite only — nothing invented. */
export function canonicalise(text) {
  let s = String(text || '');
  // DECIMALS FIRST. Doing this after the word->digit table produced "0 point 0 five five" from
  // "nought point zero five five": "nought"/"zero" had already become "0", so the decimal pattern
  // no longer matched its own input. Numbers are the least reliable part of any transcript and
  // several IS Code criteria turn on them, so this runs before anything else can disturb it.
  const w2d = (t) => (NUMWORD[String(t).toLowerCase()] ?? t);
  s = s.replace(new RegExp(`\\b(${DIGITS})\\s+point((?:\\s+${DIGITS}){1,6})`, 'gi'),
    (_, whole, rest) => `${w2d(whole)}.` + rest.trim().split(/\s+/).map(w2d).join(''));
  for (const [re, to] of CANON) s = s.replace(re, to);
  return s.replace(/\s+([.,])/g, '$1').replace(/\s{2,}/g, ' ').trim();
}

/**
 * Risk flags. `clause` is the corpus clause the answer will be graded against, when known — it lets
 * us detect a DROPPED NEGATION, which is ASR's most dangerous common failure.
 */
export function riskFlags(text, { confidence = null, alternatives = [], clause = '' } = {}) {
  const s = String(text || '').toLowerCase();
  const flags = [];

  for (const [a, b] of INVERTING_PAIRS) {
    const hasA = s.includes(a), hasB = s.includes(b);
    if (hasA || hasB) {
      const heard = hasA ? a : b, other = hasA ? b : a;
      // an alternative transcript disagreeing on this term is direct evidence of uncertainty
      const altDisagrees = alternatives.some((x) => String(x).toLowerCase().includes(other));
      flags.push({ type: 'inverting_term', heard, could_be: other,
                   evidence: altDisagrees ? 'an alternative transcript contains the opposite term' : 'term is in a meaning-inverting pair',
                   severity: altDisagrees ? 'high' : 'medium' });
    }
  }

  // dropped negation: the clause is a prohibition but the answer carries no negation at all
  if (clause && NEGATIONS.test(clause) && !NEGATIONS.test(s)) {
    flags.push({ type: 'possible_dropped_negation', heard: null, could_be: 'not / never',
                 evidence: 'the rule being graded is stated as a prohibition, but the answer contains no negation — recognisers drop short function words',
                 severity: 'high' });
  }

  if (confidence !== null && confidence < 0.75) {
    flags.push({ type: 'low_confidence', evidence: `recogniser confidence ${confidence}`, severity: confidence < 0.6 ? 'high' : 'medium' });
  }

  if (/\d/.test(s)) {
    flags.push({ type: 'contains_number', evidence: 'numbers are the least reliable part of a transcript and several criteria turn on them', severity: 'low' });
  }

  return flags;
}

/**
 * The gate. Returns either a canonical answer safe to grade, or a CONFIRM action naming exactly what
 * to ask. Never guesses which of an inverting pair was meant.
 */
export function safeToGrade(text, opts = {}) {
  const canonical = canonicalise(text);
  const flags = riskFlags(canonical, opts);
  const blocking = flags.filter((f) => f.severity === 'high' ||
    (f.severity === 'medium' && f.type === 'inverting_term'));
  if (!blocking.length) return { ok: true, canonical, flags, channel: opts.channel || 'unknown' };

  const questions = blocking.map((f) =>
    f.type === 'possible_dropped_negation'
      ? 'Did you mean that it MUST be done, or that it must NOT be done?'
      : f.type === 'low_confidence'
        ? 'I did not catch that clearly — could you say it again, or type it?'
        : `Did you say "${f.heard}" or "${f.could_be}"?`);

  return {
    ok: false, action: 'confirm', canonical, flags, questions: [...new Set(questions)],
    why: 'One of these terms inverts the meaning of the answer. Grading now could flag a correct candidate as dangerously wrong, so the answer is not graded until it is confirmed.',
    channel: opts.channel || 'unknown',
  };
}

/* Guarded — imported by drawing-check.mjs, which now runs in a browser too. */
if (typeof process !== 'undefined' && process.argv && import.meta.url === `file://${process.argv[1]}`) {
  const [, , cmd, text, conf] = process.argv;
  const out = cmd === 'check'
    ? safeToGrade(text, { confidence: conf ? +conf : null, channel: 'cli' })
    : { usage: 'answer-input.mjs check "<transcript>" [confidence]' };
  console.log(JSON.stringify(out, null, 2));
}
